import React from 'react';
import { Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  TableCell,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import HealWoundPopover from './HealWoundPopover';
import WoundThumbnails from './WoundThumbnails';
import type { WoundImage } from './useWoundImages';
import type { WoundResponse } from '../../services/api';
import { formatWoundOnset, getWoundTypeLabel } from '../../constants/wounds';
import { contradictionAnchor } from '../oasis/contradictions/contradictionNavigation';

interface WoundTableRowProps {
  wound: WoundResponse;
  assessmentCount: number;
  /** Photos logged for this wound; omitted while they are still loading. */
  images?: WoundImage[];
  canEdit?: boolean;
  canDiscontinue?: boolean;
  onEdit: (wound: WoundResponse) => void;
  onDelete: (wound: WoundResponse) => void;
  onDiscontinue: (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => Promise<void>;
  onOpenAssessments: (wound: WoundResponse) => void;
  /** Opens the photo gallery at this position in `images`. */
  onPreviewImage?: (index: number) => void;
  /** 'row' renders a desktop table row; 'card' renders a mobile stacked card. */
  variant?: 'row' | 'card';
}

const WoundTableRow: React.FC<WoundTableRowProps> = ({
  wound,
  assessmentCount,
  images,
  canEdit = true,
  canDiscontinue = false,
  onEdit,
  onDelete,
  onDiscontinue,
  onOpenAssessments,
  onPreviewImage,
  variant = 'row',
}) => {
  const isDiscontinued = !!wound.discontinued_date;

  // Row-level contradiction jump target (EMR-931). WoundsTable renders the row
  // or the card variant depending on viewport, so the anchor goes on each. The
  // navigation helper still filters to visible nodes.
  const contradictionAnchorProps = contradictionAnchor('wound_list', wound.id);

  // Shared rendering pieces reused by both the desktop row and the mobile card
  // so handlers stay identical across variants.
  const statusBadge = isDiscontinued ? (
    <Badge variant='secondary' className='text-xs'>
      Discontinued
    </Badge>
  ) : (
    <Badge
      variant='outline'
      className='text-xs bg-green-50 text-green-700 border-green-200'
    >
      Active
    </Badge>
  );

  const woundDetails = (
    <div className='space-y-0.5'>
      <p
        className={
          isDiscontinued ? 'italic text-muted-foreground' : 'font-medium'
        }
      >
        {wound.location}
      </p>
      <p className='text-xs text-muted-foreground'>
        {getWoundTypeLabel(wound.wound_type)}
      </p>
    </div>
  );

  const thumbnails = images ? (
    <WoundThumbnails
      images={images}
      onPreview={index => onPreviewImage?.(index)}
    />
  ) : null;

  const assessmentsButton = (
    <Button
      variant='outline'
      size='sm'
      onClick={() => onOpenAssessments(wound)}
    >
      Assessments ({assessmentCount})
    </Button>
  );

  const actions = (
    <div className='flex flex-wrap items-center gap-1.5'>
      {!isDiscontinued && canDiscontinue && (
        <HealWoundPopover
          minDate={new Date(wound.created_at)}
          onDiscontinue={(date, reason, comments) =>
            onDiscontinue(wound.id, date, reason, comments)
          }
          triggerLabel='Discontinue'
        />
      )}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant='outline'
                size='sm'
                onClick={() => onEdit(wound)}
                disabled={!canEdit}
              >
                Edit
              </Button>
            </span>
          </TooltipTrigger>
          {!canEdit && (
            <TooltipContent>
              <p>Cannot edit because the visit is not in an editable state</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant='outline'
                size='sm'
                onClick={() => onDelete(wound)}
                disabled={!canEdit || assessmentCount > 0}
              >
                <Trash2 className='h-4 w-4 text-destructive mr-1' />
                Delete
              </Button>
            </span>
          </TooltipTrigger>
          {(!canEdit || assessmentCount > 0) && (
            <TooltipContent>
              <p>
                {!canEdit
                  ? 'Cannot edit because the visit is not in an editable state'
                  : 'Cannot delete a wound with assessments'}
              </p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  if (variant === 'card') {
    return (
      <div
        {...contradictionAnchorProps}
        className={`rounded-md border p-3 space-y-3 ${
          isDiscontinued ? 'opacity-60' : ''
        }`}
      >
        <div className='flex items-start justify-between gap-2'>
          <div className='min-w-0'>{woundDetails}</div>
          {statusBadge}
        </div>
        <div className='flex flex-wrap gap-x-6 gap-y-2 text-sm'>
          <div className='flex flex-col'>
            <span className='text-xs text-muted-foreground'>Onset</span>
            <span className='text-muted-foreground'>
              {formatWoundOnset(wound)}
            </span>
          </div>
          <div className='flex flex-col'>
            <span className='text-xs text-muted-foreground'>Assessments</span>
            <span>{assessmentsButton}</span>
          </div>
          {/* Same empty-vs-loading rule as the desktop Photos cell: omit the
              field while images are undefined, then show a dash once empty is
              confirmed so "no photos" is not mistaken for still loading. */}
          {images !== undefined && (
            <div className='flex flex-col gap-1'>
              <span className='text-xs text-muted-foreground'>Photos</span>
              {images.length === 0 ? (
                <span className='text-muted-foreground'>—</span>
              ) : (
                thumbnails
              )}
            </div>
          )}
        </div>
        <div className='flex flex-wrap justify-end gap-1.5 border-t pt-2'>
          {actions}
        </div>
      </div>
    );
  }

  return (
    <TableRow
      {...contradictionAnchorProps}
      className={isDiscontinued ? 'opacity-60' : undefined}
    >
      <TableCell>{statusBadge}</TableCell>
      <TableCell className='text-sm'>{woundDetails}</TableCell>
      <TableCell>
        {/* A dash only once the photos have loaded, so an empty wound reads as
            empty rather than as still loading. */}
        {images?.length === 0 ? (
          <span className='text-muted-foreground'>—</span>
        ) : (
          thumbnails
        )}
      </TableCell>
      <TableCell className='text-sm text-muted-foreground'>
        {formatWoundOnset(wound)}
      </TableCell>
      <TableCell>{assessmentsButton}</TableCell>
      <TableCell>{actions}</TableCell>
    </TableRow>
  );
};

export default WoundTableRow;
